import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, ShieldCheck, Sparkles, PoundSterling } from "lucide-react";
import Seo from "@/components/Seo";
import { Marquee } from "@/components/Marquee";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { COMPANY, SERVICES, GALLERY } from "@/data/site";

const CHAPTERS = [
  {
    num: "01",
    title: "We prepare, properly",
    text: "Every great finish starts before the first coat. We fill, sand, prime and protect — floors, furniture and fixtures — so the only thing we leave behind is beautiful paint.",
  },
  {
    num: "02",
    title: "We paint with precision",
    text: "Razor-sharp edges, even coverage, no drips, no mess. Walls, ceilings, frames and skirting — every surface treated like it's our own home.",
  },
  {
    num: "03",
    title: "We keep it affordable",
    text: "Premium craftsmanship shouldn't cost the earth. With us your home becomes a paradise — at a price that makes you smile.",
  },
];

const TESTIMONIALS = [
  {
    quote: "They transformed our bedroom in two days — the grey finish is flawless and the skirting lines are perfect.",
    name: "Sarah M.",
    area: "Werneth, Oldham",
  },
  {
    quote: "Professional, tidy and genuinely affordable. The ceilings and frames look brand new.",
    name: "David K.",
    area: "Chadderton",
  },
  {
    quote: "From quote to clean-up, everything was smooth. Our home finally feels like the paradise we imagined.",
    name: "The Ahmed Family",
    area: "Oldham",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <main data-testid="home-page">
      <Seo
        title="Eunice 25 — Painting & Decorating in Oldham | Colour Your Homes Classically"
        siteName="Eunice 25"
        description="Professional painting & decorating in Werneth, Oldham. Interior walls, ceilings, frames, skirting, staining & varnishing — totally affordable. Call +44 7474 891884."
        image="/assets/work2.jpg"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Eunice 25",
          description: "Painting and decorating company in Werneth, Oldham.",
          telephone: "+44 7474 891884",
          email: "owoeunice@yahoo.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "4 Derby Street, Werneth",
            addressLocality: "Oldham",
            postalCode: "OL9 7BW",
            addressCountry: "GB",
          },
        }}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative bg-[#121212] text-[#fbf9f5] grain overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-20 lg:pt-44 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div style={{ y: textY }} className="lg:col-span-7">
            <MaskedLine delay={0.1}>
              <span className="font-mono2 text-xs sm:text-sm tracking-[0.35em] uppercase text-[#c8102e]">
                Painting & Decorating · Werneth, Oldham
              </span>
            </MaskedLine>
            <h1 className="mt-6 font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
              <MaskedLine delay={0.25}>COLOUR YOUR</MaskedLine>
              <MaskedLine delay={0.4}>
                <span className="text-[#c8102e] italic">HOMES</span>
              </MaskedLine>
              <MaskedLine delay={0.55}>CLASSICALLY.</MaskedLine>
            </h1>
            <MaskedLine delay={0.75}>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-[#fbf9f5]/70 leading-relaxed">
                Make your dreams a reality. With {COMPANY.name}, your home becomes a paradise —
                and we are totally affordable.
              </p>
            </MaskedLine>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-quote-btn"
                className="group flex items-center gap-2 bg-[#c8102e] hover:bg-[#a00d24] text-white font-bold px-8 py-4 rounded-full transition-colors"
              >
                Get a Free Quote
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/gallery"
                data-testid="hero-gallery-btn"
                className="flex items-center gap-2 border border-[#fbf9f5]/30 hover:border-[#fbf9f5] text-[#fbf9f5] font-bold px-8 py-4 rounded-full transition-colors"
              >
                See Our Work
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-3 border border-[#c8102e]/40 rounded-2xl rotate-2" />
              <img
                src="/assets/hero.jpg"
                alt="Eunice 25 painter beautifully painting a wall by hand"
                className="relative rounded-2xl w-full aspect-[4/5] object-cover shadow-2xl"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#c8102e] text-white rounded-2xl px-6 py-4 shadow-xl">
                <p className="font-display font-black text-3xl">5+</p>
                <p className="font-mono2 text-[10px] tracking-[0.2em] uppercase opacity-80">Core Services</p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-10 flex flex-wrap gap-x-10 gap-y-3 font-mono2 text-xs tracking-[0.2em] uppercase text-[#fbf9f5]/40">
          <span>EST. OL9 7BW</span>
          <span>Fully Insured</span>
          <span>Free Quotes</span>
          <span>Domestic & Commercial</span>
        </div>
      </section>

      <Marquee />

      {/* MANIFESTO CHAPTERS */}
      <section className="bg-[#fbf9f5] py-24 sm:py-32" data-testid="manifesto-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-[#c8102e]">The Eunice 25 Promise</p>
            <h2 className="mt-4 font-display font-black tracking-tight leading-snug text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
              Three chapters. <span className="italic text-[#c8102e]">One flawless finish.</span>
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.12} className="bg-[#fbf9f5]">
                <div className="p-8 sm:p-10 h-full group hover:bg-[#f3efe6] transition-colors" data-testid={`chapter-${c.num}`}>
                  <span className="font-display font-black text-5xl text-outline-ink group-hover:text-[#c8102e] group-hover:[-webkit-text-stroke:0] transition-all">
                    {c.num}
                  </span>
                  <h3 className="mt-6 text-xl sm:text-2xl font-bold tracking-tight">{c.title}</h3>
                  <p className="mt-4 text-base text-neutral-600 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="bg-[#121212] text-[#fbf9f5] py-24 sm:py-32 grain relative" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-[#c8102e]">Our Service</p>
              <h2 className="mt-4 font-display font-black tracking-tight leading-snug text-2xl sm:text-3xl lg:text-4xl">
                Everything your home needs, <span className="italic text-[#c8102e]">painted perfectly.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/services" data-testid="services-view-all-link" className="group flex items-center gap-2 font-bold text-sm hover:text-[#c8102e] transition-colors">
                All Services <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </Reveal>
          </div>

          <div className="mt-14 border-t border-white/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                <Link
                  to="/services"
                  data-testid={`service-row-${s.id}`}
                  className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 sm:gap-8 py-6 sm:py-8 border-b border-white/10 hover:bg-white/[0.04] transition-colors px-2 sm:px-4"
                >
                  <span className="font-mono2 text-sm text-[#c8102e]">{s.id}</span>
                  <span className="font-display font-bold text-xl sm:text-3xl tracking-tight group-hover:translate-x-2 transition-transform duration-300">
                    {s.title}
                  </span>
                  <ArrowUpRight className="w-5 h-5 sm:w-7 sm:h-7 text-[#fbf9f5]/30 group-hover:text-[#c8102e] group-hover:rotate-45 transition-all duration-300" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED WORK SPOTLIGHT */}
      <section className="bg-[#f3efe6] py-24 sm:py-32 overflow-hidden" data-testid="featured-work-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-[#c8102e]">Recent Work</p>
            <h2 className="mt-4 font-display font-black tracking-tight leading-snug text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
              Real rooms. <span className="italic text-[#c8102e]">Real transformations.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {GALLERY.filter((g) => g.real).slice(0, 6).map((g, i) => (
              <Reveal key={g.id} delay={i * 0.12}>
                <Link to="/gallery" data-testid={`featured-work-${g.id}`} className="group block relative overflow-hidden rounded-2xl">
                  <img
                    src={g.src}
                    alt={g.title}
                    className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 p-6 sm:p-8">
                    <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#c8102e] bg-white/90 inline-block px-3 py-1 rounded-full">
                      {g.category}
                    </p>
                    <h3 className="mt-3 font-display font-bold text-2xl text-white">{g.title}</h3>
                    <p className="font-mono2 text-xs text-white/70 mt-1">{g.spec}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US STRIP */}
      <section className="bg-[#fbf9f5] py-20 border-y border-black/10" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-10">
          {[
            { icon: ShieldCheck, title: "Tidy & Insured", text: "Dust sheets down, furniture protected, everything spotless when we leave." },
            { icon: PoundSterling, title: "Totally Affordable", text: "Honest quotes, no hidden extras — premium finish at a fair price." },
            { icon: Sparkles, title: "Paradise Finish", text: "We treat every home like our own until it feels like paradise." },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.1}>
              <div className="flex gap-4" data-testid={`why-us-${i}`}>
                <span className="shrink-0 w-12 h-12 rounded-full bg-[#c8102e]/10 flex items-center justify-center">
                  <f.icon className="w-5 h-5 text-[#c8102e]" />
                </span>
                <div>
                  <h3 className="font-bold text-lg tracking-tight">{f.title}</h3>
                  <p className="mt-1 text-sm text-neutral-600 leading-relaxed">{f.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#fbf9f5] py-24 sm:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-[#c8102e]">Kind Words</p>
            <h2 className="mt-4 font-display font-black tracking-tight leading-snug text-2xl sm:text-3xl lg:text-4xl">
              Loved across <span className="italic text-[#c8102e]">Oldham.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.12}>
                <figure className="bg-white border border-black/10 rounded-2xl p-8 h-full flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`testimonial-${i}`}>
                  <div className="flex gap-1 text-[#c8102e]">
                    {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-current" />)}
                  </div>
                  <blockquote className="mt-5 font-display italic text-lg leading-relaxed flex-1">“{t.quote}”</blockquote>
                  <figcaption className="mt-6 font-mono2 text-xs tracking-[0.15em] uppercase text-neutral-500">
                    {t.name} · {t.area}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="bg-[#c8102e] text-white grain relative overflow-hidden" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
          <Reveal>
            <h2 className="font-display font-black tracking-tight leading-tight text-3xl sm:text-5xl">
              Ready to colour your home <span className="italic">classically?</span>
            </h2>
            <p className="mt-4 text-white/80 text-base sm:text-lg max-w-xl mx-auto">
              Call us today or send an enquiry — free quotes, friendly advice, zero pressure.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href={COMPANY.phone1Href}
                data-testid="cta-call-btn"
                className="bg-white text-[#c8102e] font-bold px-8 py-4 rounded-full hover:bg-[#f3efe6] transition-colors"
              >
                Call {COMPANY.phone1}
              </a>
              <Link
                to="/contact"
                data-testid="cta-contact-btn"
                className="border-2 border-white text-white font-bold px-8 py-4 rounded-full hover:bg-white/10 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
